import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';
// Re-exported for convenience; the implementation lives in utils so that it
// can be used without importing this module's axios client.
export { parseVerificationError } from '../utils/verificationErrors';

const getToken = () => {
  return localStorage.getItem('token');
};

const clientVerificationApiClient = axios.create({
  baseURL: config.api.clientVerificationServiceUrl || 'http://localhost:8023',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

clientVerificationApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out.
// Note this is why a wrong verification code is a 400 and not a 401 - a 401
// here would be read as an expired session and could bounce the advisor to
// /login in the middle of a client call.
applyAuthRetryInterceptor(clientVerificationApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(clientVerificationApiClient);

const clientVerificationService = {
  /** The service base URL, so failures can name what could not be reached. */
  baseUrl: () => clientVerificationApiClient.defaults.baseURL,

  /**
   * Search Tamarac CRM for contacts to verify.
   *
   * Tamarac is the system of record and nothing syncs it into our own client
   * table, so the picker reads it live. Results carry masked destinations only.
   *
   * Errors are thrown rather than swallowed into an empty list on purpose: the
   * service answers a directory outage with a 502 precisely so the page can
   * tell "no such client" apart from "the CRM is down", and returning [] here
   * would throw that distinction away again.
   */
  searchClients: async (query, limit = 25) => {
    const response = await clientVerificationApiClient.get(
      'client-verification/clients/search',
      { params: { query, limit } }
    );
    return {
      clients: Array.isArray(response.data?.clients) ? response.data.clients : [],
      source: response.data?.source || 'tamarac',
      // Contacts that matched but were withheld: no Tamarac contact id, or
      // nothing to send to. Surfaced so a client whose CRM record is merely
      // incomplete cannot read as a client who does not exist.
      unverifiable: response.data?.unverifiable || 0,
      unverifiableNote: response.data?.unverifiable_note || ''
    };
  },

  /**
   * Generate a code and deliver it to the client.
   *
   * The response deliberately does not contain the code. `destinationMasked` is
   * the masked value on screen: the service re-resolves the real destination
   * from Tamarac and refuses the send if it no longer matches, so a record
   * edited between picking and sending cannot deliver somewhere the advisor was
   * never shown.
   */
  sendCode: async ({
    contactId, channel = 'phone', displayName = '', destinationMasked = '', reason = null
  }) => {
    try {
      const response = await clientVerificationApiClient.post('client-verification/send-code', {
        contact_id: contactId,
        channel,
        display_name: displayName,
        destination_masked: destinationMasked,
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error sending verification code:', error);
      throw error;
    }
  },

  /** Check the code the client read back over the phone. */
  verifyCode: async ({ verificationId, code }) => {
    try {
      const response = await clientVerificationApiClient.post('client-verification/verify', {
        verification_id: verificationId,
        code
      });
      return response.data;
    } catch (error) {
      // Expected outcomes (wrong code, expired, locked) arrive here as 4xx.
      // The caller inspects them via parseVerificationError.
      throw error;
    }
  },

  /** Destroy an outstanding code. Idempotent. */
  cancelCode: async (verificationId) => {
    try {
      const response = await clientVerificationApiClient.post('client-verification/cancel', {
        verification_id: verificationId
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling verification code:', error);
      throw error;
    }
  },

  /** Countdown state; also recovers an in-flight verification after a refresh. */
  fetchStatus: async (verificationId) => {
    try {
      const response = await clientVerificationApiClient.get(
        `client-verification/status/${encodeURIComponent(verificationId)}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /** Audit history: per client, per staff member, or recent overall. */
  fetchEvents: async ({ clientId = null, staffUserId = null, limit = 25 } = {}) => {
    try {
      const params = { limit };
      if (clientId) params.client_id = clientId;
      if (staffUserId) params.staff_user_id = staffUserId;
      const response = await clientVerificationApiClient.get('client-verification/events', {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching verification history:', error);
      throw error;
    }
  }
};

export default clientVerificationService;
